import type { PriceBasis } from '@/features/trading-chart/api/chartApi'

/**
 * The symbol a member last charted, so the sidebar's bare "Chart" row opens
 * where they left off rather than on a stranger's default.
 *
 * `localStorage`, for the reasons features/brokers/lib/lastUsed.ts gives for
 * its own: it must outlive the tab and it is read by a route loader, before
 * anything has mounted to hold it. Every read and write is wrapped in
 * try/catch and an unreadable or blocked store answers "none" — a lost
 * shortcut costs a default page, not a broken navigation.
 *
 * Not cleared at sign-out the way the brokers' is: a symbol is not private to
 * a member, and the worst a shared browser does is open on the previous
 * person's last stock.
 */

const LAST_SYMBOL_KEY = 'mm-chart-last-symbol-v1'

export const readLastChartSymbol = (): string | null => {
  try {
    const raw = window.localStorage.getItem(LAST_SYMBOL_KEY)
    return raw === null || raw === '' ? null : raw
  } catch {
    return null
  }
}

export const rememberLastChartSymbol = (symbol: string): void => {
  try {
    window.localStorage.setItem(LAST_SYMBOL_KEY, symbol)
  } catch {
    // Blocked or full storage costs a remembered shortcut next time, not the
    // chart on screen now.
  }
}

const PRICE_BASIS_KEY = 'mm-chart-price-basis-v1'

/**
 * The prices the member last chose to chart (the header's Adj / Unadj), read by the page loader
 * for its first bars. Anything but a remembered "traded" is the default, adjusted.
 */
export const readChartPriceBasis = (): PriceBasis => {
  try {
    return window.localStorage.getItem(PRICE_BASIS_KEY) === 'traded' ? 'traded' : 'adjusted'
  } catch {
    return 'adjusted'
  }
}

export const rememberChartPriceBasis = (basis: PriceBasis): void => {
  try {
    window.localStorage.setItem(PRICE_BASIS_KEY, basis)
  } catch {
    // The choice holds for this chart; the next visit opens on adjusted prices.
  }
}
